import React from 'react';
import { styled } from 'styled-components';

interface PhotoTileProps {
  photoId: string;
  photoUrl: string;
  isSelected: boolean;
  isRepresentative: boolean;
  onClick: (photoId: string, event: React.MouseEvent) => void;
  stackCount?: number;
  onStackClick?: (photoId: string) => void;
}

const Tile = styled.div<{ $selected: boolean; $stacked: boolean }>`
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 6px;
  overflow: ${(p) => (p.$stacked ? 'visible' : 'hidden')};
  cursor: pointer;
  border: 2px solid ${(p) => (p.$selected ? '#3182ce' : 'transparent')};
  box-shadow: ${(p) => (p.$selected ? '0 0 0 2px #3182ce' : '0 1px 3px rgba(0,0,0,0.1)')};
  transition: box-shadow 0.1s, border-color 0.1s;
  flex-shrink: 0;

  ${(p) =>
    p.$stacked &&
    `
    &::before, &::after {
      content: '';
      position: absolute;
      border-radius: 6px;
      background: #e2e8f0;
      z-index: -1;
    }
    &::before {
      inset: 3px -3px -3px 3px;
      background: #cbd5e0;
    }
    &::after {
      inset: 6px -6px -6px 6px;
      background: #e2e8f0;
    }
  `}

  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
`;

const TileInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StarBadge = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6);
  color: #fbd38d;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
`;

const StackBadge = styled.div`
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 600;
  z-index: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const PhotoTile: React.FC<PhotoTileProps> = ({
  photoId,
  photoUrl,
  isSelected,
  isRepresentative,
  onClick,
  stackCount,
  onStackClick,
}) => {
  const isStacked = !!stackCount && stackCount > 1;
  return (
    <Tile
      $selected={isSelected}
      $stacked={isStacked}
      onClick={(e) => onClick(photoId, e)}
    >
      <TileInner>
        <Img src={photoUrl} alt={`Photo ${photoId}`} loading="lazy" />
      </TileInner>
      {isRepresentative && <StarBadge>★</StarBadge>}
      {isStacked && (
        <StackBadge
          onClick={(e) => {
            e.stopPropagation();
            onStackClick?.(photoId);
          }}
        >
          {stackCount}
        </StackBadge>
      )}
    </Tile>
  );
};

export default PhotoTile;
